/* ============================================================
   errors.js — the API error type and FastAPI `detail` unwrapping.

   Lifted verbatim from the original src/api.js. FastAPI returns errors as
   {detail: ...} where `detail` is a string for HTTPException and an array of
   {loc, msg, type} objects for request-validation failures — parseDetail
   already handles both shapes, so it moves unchanged.
   ============================================================ */
import { moduleLabel } from '../auth/moduleLabels'

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/** Raised when the refresh token itself is rejected — the session is over. */
export class SessionExpiredError extends Error {
  constructor(message = 'Your session has expired. Please sign in again.') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

/* FastAPI request-validation item {loc, type, msg, input} → a sentence a user can act on:
   ["body","position","department_id"] + null → "Department is required". The raw pydantic
   msg ("UUID input should be a string, bytes or UUID object") means nothing to a user. */
function fieldLabel(loc) {
  const name = [...(loc || [])].reverse().find((p) => typeof p === 'string' && !['body', 'query', 'path'].includes(p))
  if (!name) return 'A field'
  const words = name.replace(/_ids?$/, (m) => (m === '_ids' ? 's' : '')).replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function validationItemMessage(item) {
  if (!item || typeof item !== 'object' || !Array.isArray(item.loc) || !item.type) return null
  const label = fieldLabel(item.loc)
  if (item.type === 'missing' || item.input == null || item.input === '') return `${label} is required`
  if (item.type === 'value_error' && item.msg) return `${label}: ${String(item.msg).replace(/^Value error,\s*/i, '')}`
  return `${label} has an invalid value`
}

export function parseDetail(detail) {
  if (detail == null || detail === '') return null
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object') {
          return validationItemMessage(item) || item.msg || item.message || JSON.stringify(item)
        }
        return String(item)
      })
      .filter(Boolean)
    return [...new Set(parts)].join('; ')
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail)
  }
  return String(detail)
}

// Backend 403 wording from require_permission (users/permissions.py): one tag,
// several tags (AND), or any-of.
const PERMISSION_DENIED_RE = /^Missing (?:any of )?required permissions?:\s*(.+)$/i

/** Tags named by a "Missing required permission(s): …" message, else null. */
export function parsePermissionDenial(message) {
  if (typeof message !== 'string') return null
  const match = PERMISSION_DENIED_RE.exec(message.trim())
  if (!match) return null
  const tags = match[1].split(',').map((t) => t.trim()).filter(Boolean)
  return tags.length ? tags : null
}

/** "hiring_managers.view" -> "Recruiter Managers · view" — the sidebar name, as Access Control shows it. */
export function describePermissionTag(tag) {
  const [module, action] = String(tag).split('.')
  const label = moduleLabel(module)
  return action ? `${label} · ${action}` : label
}

/* ============================================================
   USER-FACING ERROR COPY
   The API answers failures with `detail: str(e)`, so a 500 can carry a raw
   SQLAlchemy/asyncpg exception — SQL, schema and table names included. That
   text must never reach a user: it is meaningless to them and it leaks
   internals. Rules:
     - network / timeout / 5xx: always our own wording, never the server text;
     - 4xx: the server's detail when it is a plain human sentence (those are
       written for users: "Job not found", "Email already registered"),
       otherwise a status-specific default;
     - the caller's `context` ("Could not save settings.") leads the message,
       so the user knows WHAT failed as well as why.
   ============================================================ */

const GENERIC = 'Something went wrong. Please try again.'

// Fingerprints of exception text, stack traces and SQL. Anything matching is
// treated as internal and replaced with our own copy.
const INTERNAL_RE = new RegExp([
  'sqlalchemy', 'asyncpg', 'psycopg', 'postgres', 'traceback', 'exception', 'errno',
  '\\[sql', '\\bselect\\b.+\\bfrom\\b', '\\binsert into\\b', '\\bupdate\\b.+\\bset\\b', '\\bdelete from\\b',
  'violates', 'duplicate key', 'relation "', 'column "', 'operator does not exist',
  'nonetype', 'object has no attribute', 'keyerror', 'valueerror', 'typeerror', 'attributeerror',
  'integrityerror', 'programmingerror', 'operationalerror', 'cannot read propert', 'undefined is not',
  'file "', '\\bline \\d+', 'https?://', '<class ',
].join('|'), 'i')

/** True when `text` reads like a sentence written for a user, not an internal error. */
export function isUserSafeText(text) {
  if (typeof text !== 'string') return false
  const t = text.trim()
  return t.length > 0 && t.length <= 220 && !t.includes('\n') && !INTERNAL_RE.test(t)
}

const join = (context, reason) => (context ? `${context.replace(/\s*$/, '')} ${reason}` : reason)

/**
 * The one message to show a user for a failed request (or any thrown error).
 * `context` is optional and says what was being attempted.
 */
export function userMessage(err, context) {
  if (err instanceof SessionExpiredError) return err.message
  if (!(err instanceof ApiError)) {
    // Hand-thrown validation errors are human; runtime TypeErrors are not.
    return isUserSafeText(err?.message) ? err.message : join(context, GENERIC)
  }

  const raw = parseDetail(err.body?.detail)
  const fromServer = isUserSafeText(raw) ? raw : null
  const status = err.status

  if (!status) return join(context, 'We couldn’t reach the server. Check your internet connection and try again.')
  if (status === 408 || status === 504) return join(context, 'The server took too long to respond. Please try again.')
  if (status === 429) return join(context, 'Too many requests right now. Please wait a moment and try again.')
  if (status === 502 || status === 503) return join(context, 'The service is temporarily unavailable. Please try again in a few minutes.')
  if (status >= 500) return join(context, 'Something went wrong on our side. Please try again in a moment — if it keeps happening, contact support.')

  if (status === 403) {
    const tags = parsePermissionDenial(raw)
    if (tags) return `You don’t have permission to do this (needs: ${tags.map(describePermissionTag).join(', ')}).`
  }
  // Field-level validation (422 list): say what failed, then which fields.
  if (fromServer && status === 422 && Array.isArray(err.body?.detail)) return join(context, `${fromServer}.`)
  if (fromServer) return fromServer
  switch (status) {
    case 400: case 422: return join(context, 'Please check the details and try again.')
    case 401: return 'Your session has expired. Please sign in again.'
    case 403: return 'You don’t have permission to do this.'
    case 404: return join(context, 'We couldn’t find it — it may have been moved or deleted.')
    case 409: return join(context, 'It conflicts with an existing record. Refresh and try again.')
    case 413: return join(context, 'The file is too large to upload.')
    default: return join(context, GENERIC)
  }
}

/**
 * Kept under its historical name — 170+ call sites pass (err, 'Could not …').
 * It is no longer auth-specific: login wording lives in authErrorMessage.
 */
export function friendlyAuthError(err, context) {
  return userMessage(err, context)
}

/** Sign-in / sign-up / reset screens: same safety rules, auth-specific 4xx wording. */
export function authErrorMessage(err, context) {
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
    const raw = parseDetail(err.body?.detail)
    if (isUserSafeText(raw)) return raw
    const AUTH = {
      400: 'Please check your details and try again.',
      401: 'Incorrect email or password.',
      403: 'Your account is waiting for approval.',
      404: 'No account found for that email.',
      409: 'An account with that email already exists.',
      429: 'Too many attempts. Please wait a moment and try again.',
    }
    if (AUTH[err.status]) return AUTH[err.status]
  }
  return userMessage(err, context)
}
